/**
 * Rendu des blocs d'une leçon, partagé par le mode « pas à pas » (LessonPlayer) et le mode
 * « défilement » (CardFeed). Un seul rendu : l'élève voit la même figure dans les deux modes.
 */
import "mafs/core.css";
import "katex/dist/katex.min.css";
import katex from "katex";
import { Suspense, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { Coordinates, Mafs, Plot, Point, Text, useMovablePoint } from "mafs";
import { compileExpr, initialParams, readFigureSpec, readGeometrySpec, SolidSpec, type FigureSpec, type LessonBlock, type LessonContent } from "@jange/shared";
import { GeometryFigure } from "./geometry.js";
import { MarkedImage } from "./image.js";
import type { Solid3DProps } from "./three/Solid3D.js";
import type { Model3DProps } from "./three/Model3D.js";
import { C, fmt, SliderRow } from "./theme.js";

/**
 * La 3D (three.js, ~250 Ko compressés) est injectée par l'appelant : le studio la charge à la demande,
 * l'app utilise un composant séparé réservé aux leçons qui en contiennent. Sans elle, le lecteur
 * reste léger et affiche un message à la place des blocs 3D.
 */
export type ThreeComponents = { Solid3D: ComponentType<Solid3DProps>; Model3D: ComponentType<Model3DProps> };
const No3D = () => <div style={{ padding: 12, borderRadius: 12, background: C.amberTint, color: C.amberText, fontSize: 13 }}>Contenu 3D : ouvre la leçon depuis l'app à jour.</div>;
const Loading3D = () => <div style={{ height: 280, borderRadius: 18, border: `1px solid ${C.border}`, display: "grid", placeItems: "center", color: C.muted, fontSize: 13 }}>Chargement de la 3D…</div>;

export type PlayerEvent =
  | { type: "STEP_CHANGED"; stepId: string; index: number }
  | { type: "PARAM_CHANGED"; name: string; value: number }
  | { type: "POINT_MOVED"; x: number; y: number }
  | { type: "SCENE_ROTATED"; blockId: string }
  | { type: "HOTSPOT_OPENED"; blockId: string; hotspotId: string }
  | { type: "QUICK_CHECK"; blockId: string; correct: boolean }
  | { type: "CARD_VIEWED"; stepId: string; index: number; dwellMs: number }
  | { type: "HELP_REQUESTED"; stepId: string }
  | { type: "REVIEW_ANSWERED"; cardKey: string; notionId: string; correct: boolean }
  | { type: "LESSON_COMPLETED" }
  | { type: "PRACTICE_REQUESTED" };

export function Formula({ latex, display }: { latex: string; display: boolean }) {
  const html = useMemo(() => katex.renderToString(latex, { throwOnError: false, displayMode: display }), [latex, display]);
  return <div style={{ fontSize: 18, textAlign: display ? "center" : "left", margin: "4px 0" }} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Markdown minimal : **gras**, *italique*. Suffisant pour les leçons ; pas de HTML venant du contenu. */
export function md(s: string) {
  const esc = s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  return esc.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/\*(.+?)\*/g, "<i>$1</i>");
}

const axisStep = (span: number) => [1, 2, 5, 10, 20, 50, 100, 200].find((s) => span / s <= 9) ?? 500;

/** Affichage lisible d'une expression : a*x^2 → a·x², sans changer son sens. */
const prettyExpr = (e: string) => e.replace(/\s*\*\s*/g, "·").replace(/\^2(?!\d)/g, "²").replace(/\^3(?!\d)/g, "³").replace(/-/g, "−");
const CURVE_COLOR: Record<string, string> = { teal: C.teal, amber: C.amber, ink: C.ink, muted: "#C8CDD6" };

/** Figure décrite par l'enseignant : courbes y = f(x) pilotées par des curseurs, point mobile sur une courbe. */
function FunctionFigure({ spec, params, lang, onParam, onPoint }: { spec: FigureSpec; params: Record<string, number>; lang: string; onParam: (name: string, v: number) => void; onPoint: (x: number, y: number) => void }) {
  const names = useMemo(() => spec.sliders.map((s) => s.name), [spec]);
  // Expressions compilées une fois par spec (déjà validées à l'enregistrement ; une erreur donne une courbe vide).
  const fns = useMemo(() => spec.curves.map((c) => {
    const r = compileExpr(c.expr, ["x", ...names]);
    return r.ok ? r.expr.fn : () => NaN;
  }), [spec, names]);
  const evalAt = (i: number, x: number) => { const y = fns[i]?.({ ...params, x }); return Number.isFinite(y) ? (y as number) : NaN; };
  const [xMin, xMax] = spec.view.x;
  const pc = spec.point?.curve ?? 0;
  const startX = spec.point?.initialX ?? 0;
  const point = useMovablePoint([startX, Number.isFinite(evalAt(pc, startX)) ? evalAt(pc, startX) : 0], {
    constrain: ([x]) => { const cx = Math.max(xMin, Math.min(xMax, x)); const y = evalAt(pc, cx); return [cx, Number.isFinite(y) ? y : 0]; },
    color: C.amber,
  });
  useEffect(() => { if (spec.point) { const y = evalAt(pc, point.x); point.setPoint([point.x, Number.isFinite(y) ? y : 0]); } /* eslint-disable-line */ }, [JSON.stringify(params)]);
  // Graduations lisibles quelle que soit l'échelle (environ 8 par axe).
  const xStep = axisStep(spec.view.x[1] - spec.view.x[0]);
  const yStep = axisStep(spec.view.y[1] - spec.view.y[0]);
  const main = spec.curves.findIndex((c) => c.color === "teal");
  const shown = spec.curves[main >= 0 ? main : 0];

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 18, padding: 12, display: "flex", flexDirection: "column", gap: 10, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>
          <i>y</i> = {shown.label ?? prettyExpr(shown.expr)}
          {spec.sliders.length > 0 && <span style={{ color: C.muted, fontWeight: 500, fontSize: 13 }}> · {spec.sliders.map((s) => <span key={s.name}><i>{s.name}</i> = <span style={{ color: C.teal, fontWeight: 700 }}>{fmt(params[s.name] ?? s.initial)}</span> </span>)}</span>}
        </div>
        {(spec.sliders.length > 0 || spec.point) && <span style={{ fontSize: 11, fontWeight: 700, color: C.teal, background: C.tealTint, padding: "4px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>Manipulable</span>}
      </div>
      <Mafs height={260} viewBox={{ x: spec.view.x, y: spec.view.y }} pan={false} zoom={false} preserveAspectRatio={false}>
        <Coordinates.Cartesian subdivisions={xStep === 1 && yStep === 1 ? 2 : false} xAxis={{ lines: xStep, labels: (v) => fmt(v) }} yAxis={{ lines: yStep, labels: (v) => fmt(v) }} />
        {spec.curves.map((c, i) => <Plot.OfX key={i} y={(x) => evalAt(i, x)} color={CURVE_COLOR[c.color] ?? C.teal} weight={c.color === "teal" ? 3 : 2} style={c.style} />)}
        {/* Points remarquables : ordonnée calculée sur la courbe (suit les curseurs), apparition douce. */}
        {spec.marks?.map((m, i) => {
          const y = evalAt(m.curve, m.x);
          if (!Number.isFinite(y)) return null;
          return (
            <g key={`m${i}`} className="jg-mark" style={{ animationDelay: `${150 + i * 120}ms` }}>
              <Point x={m.x} y={y} color={C.amber} />
              <Text x={m.x} y={y} attach="ne" attachDistance={12} size={13} color={C.ink}>{`${m.label ? `${m.label} ` : ""}(${fmt(m.x)} ; ${fmt(y)})`}</Text>
            </g>
          );
        })}
        {spec.point && point.element}
      </Mafs>
      {spec.point && <div style={{ fontSize: 13, color: C.muted }}>Point : ({point.x.toFixed(2)} ; {point.y.toFixed(2)})</div>}
      {spec.sliders.map((s) => <SliderRow key={s.name} s={s} value={params[s.name] ?? s.initial} lang={lang} onChange={(v) => onParam(s.name, v)} onRelease={() => onPoint(point.x, point.y)} />)}
    </div>
  );
}

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Transition entre étapes : les paramètres de la figure glissent vers leur nouvelle valeur (450 ms),
 * pour que l'élève VOIE la courbe se resserrer ou se retourner. Instantané si « réduire les animations ».
 */
export function useTweenedParams(target: Record<string, number> | undefined, initial: Record<string, number>) {
  const [params, setParams] = useState<Record<string, number>>(target ?? initial);
  const current = useRef(params);
  current.current = params;
  useEffect(() => {
    if (!target) return;
    if (reducedMotion()) { setParams(target); return; }
    const from = { ...current.current };
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 450);
      const k = easeOutCubic(t);
      const next: Record<string, number> = {};
      for (const key of Object.keys(target)) next[key] = (from[key] ?? target[key]) + (target[key] - (from[key] ?? target[key])) * k;
      setParams(next);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [JSON.stringify(target)]); // eslint-disable-line react-hooks/exhaustive-deps
  return [params, setParams] as const;
}

export const PLAYER_CSS = `
/* Thème clair et sobre pour les figures (Mafs est sombre par défaut) */
.jg-player .MafsView { --mafs-bg: #FFFFFF; --mafs-fg: #15213B; --mafs-line-color: #C9CFDA; --grid-line-subdivision-color: #EEF0F4; --mafs-origin-color: #15213B; --mafs-orange: #E8A317; border-radius: 12px; }
@keyframes jg-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.jg-in { animation: jg-in 220ms cubic-bezier(.2,.8,.2,1) both; }
@keyframes jg-pop { 0% { transform: scale(1); } 45% { transform: scale(1.04); } 100% { transform: scale(1); } }
@keyframes jg-shake { 0%, 100% { transform: none; } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
.jg-pop { animation: jg-pop 260ms cubic-bezier(.2,.8,.2,1); }
.jg-shake { animation: jg-shake 220ms ease-in-out; }
@keyframes jg-mark { from { opacity: 0; } to { opacity: 1; } }
.jg-mark { animation: jg-mark 260ms ease-out both; }
@media (prefers-reduced-motion: reduce) { .jg-in, .jg-pop, .jg-shake, .jg-mark { animation: none; } }
`;


/** Paramètres des figures et solides d'une leçon : valeurs initiales, surchargées par l'étape, avec transition. */
export function useLessonParams(blocks: LessonContent["blocks"], figureParams: Record<string, number> | undefined) {
  const specs = useMemo(() => new Map(blocks.flatMap((b) => (b.type === "figure" ? [[b.id, readFigureSpec(b.spec)] as const] : []))), [blocks]);
  const solids = useMemo(() => new Map(blocks.flatMap((b) => {
    if (b.type !== "solid3d") return [];
    const r = SolidSpec.safeParse(b.spec);
    return r.success ? [[b.id, r.data] as const] : [];
  })), [blocks]);
  const defaults = useMemo(() => {
    const out: Record<string, number> = {};
    for (const b of blocks) if (b.type === "figure") Object.assign(out, initialParams(specs.get(b.id)!), b.params);
    for (const b of blocks) if (b.type === "geometry") { const g = readGeometrySpec(b.spec); if (g) for (const sl of g.sliders) out[sl.name] ??= b.params[sl.name] ?? sl.initial; }
    for (const sp of solids.values()) for (const sl of sp.sliders) out[sl.name] ??= sl.initial;
    return out;
  }, [blocks, specs, solids]);
  const target = useMemo(() => ({ ...defaults, ...figureParams }), [defaults, figureParams]);
  const [params, setParams] = useTweenedParams(target, defaults);
  return { params, setParams, specs, solids };
}

/** Question rapide : une touche, retour immédiat et doux, explication ; jamais de note. */
export function QuickCheck({ block, onAnswer, compact = false }: { block: Extract<LessonBlock, { type: "quiz" }> & { id: string }; onAnswer: (correct: boolean) => void; compact?: boolean }) {
  const [picked, setPicked] = useState<string | null>(null);
  const done = picked !== null;
  const correct = picked === block.answerId;
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 18, padding: compact ? 12 : 16, background: "#fff", display: "flex", flexDirection: "column", gap: 10 }}>
      <span style={{ alignSelf: "flex-start", fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: C.teal, background: C.tealTint, padding: "4px 8px", borderRadius: 999 }}>Question rapide</span>
      <div style={{ fontSize: compact ? 16 : 18, fontWeight: 650, lineHeight: 1.35 }} dangerouslySetInnerHTML={{ __html: md(block.question) }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {block.choices.map((c) => {
          const isPicked = picked === c.id;
          const isAnswer = c.id === block.answerId;
          const bg = !done ? "#fff" : isAnswer ? C.tealTint : isPicked ? C.amberTint : "#fff";
          const border = !done ? C.border : isAnswer ? C.teal : isPicked ? C.amber : C.border;
          return (
            <button key={c.id} disabled={done} onClick={() => { setPicked(c.id); onAnswer(c.id === block.answerId); }}
              className={isPicked ? (correct ? "jg-pop" : "jg-shake") : undefined}
              style={{ textAlign: "left", minHeight: 48, padding: "12px 14px", borderRadius: 14, border: `2px solid ${border}`, background: bg, color: C.ink, fontSize: 16, fontWeight: 600, cursor: done ? "default" : "pointer", transition: "background-color 180ms ease, border-color 180ms ease", opacity: done && !isPicked && !isAnswer ? 0.55 : 1 }}>
              {c.label}{done && isAnswer ? "  ✓" : ""}
            </button>
          );
        })}
      </div>
      {done && (
        <div className="jg-in" role="status" style={{ borderRadius: 12, padding: 12, fontSize: 14, lineHeight: 1.5, background: correct ? C.tealTint : C.amberTint, color: correct ? C.teal : C.amberText }}>
          <b>{correct ? "Bien vu !" : "Pas tout à fait."}</b>{block.explanation ? <> {block.explanation}</> : null}
        </div>
      )}
    </div>
  );
}

export type BlockContext = {
  lang: string;
  params: Record<string, number>;
  setParam: (name: string, v: number) => void;
  specs: Map<string, FigureSpec>;
  solids: Map<string, SolidSpec>;
  three?: ThreeComponents;
  resolveAsset: (id: string) => string | null;
  autoLoad3d: boolean;
  onEvent: (e: PlayerEvent) => void;
  /** Mode défilement : les blocs manipulables sont protégés pour que le glissement fasse défiler. */
  guard?: (blockId: string, node: ReactNode) => ReactNode;
  compact?: boolean;
};

const INTERACTIVE = new Set(["figure", "geometry", "solid3d", "scene3d"]);
export const isInteractive = (b: { type: string }) => INTERACTIVE.has(b.type);

export function BlockView({ block: b, ctx, animKey }: { block: LessonBlock & { id: string }; ctx: BlockContext; animKey: string }) {
  const Solid3D = ctx.three?.Solid3D ?? No3D;
  const Model3D = ctx.three?.Model3D ?? No3D;
  const guard = (node: ReactNode) => (ctx.guard && isInteractive(b) ? ctx.guard(b.id, node) : node);
  switch (b.type) {
    case "text": return <p key={animKey} className="jg-in" style={{ fontSize: ctx.compact ? 17 : 15, lineHeight: 1.5, margin: 0 }} dangerouslySetInnerHTML={{ __html: md(b.md) }} />;
    case "formula": return <Formula latex={b.latex} display={b.display} />;
    case "figure": return guard(<FunctionFigure spec={ctx.specs.get(b.id)!} params={ctx.params} lang={ctx.lang} onParam={(name, v) => { ctx.setParam(name, v); ctx.onEvent({ type: "PARAM_CHANGED", name, value: v }); }} onPoint={(x, y) => ctx.onEvent({ type: "POINT_MOVED", x, y })} />);
    case "image":
      return <MarkedImage src={ctx.resolveAsset(b.assetId)} alt={b.alt[ctx.lang] ?? b.alt.fr ?? ""} aspect={b.aspect} markers={b.markers} lang={ctx.lang} credit={b.credit ?? null}
        onMarker={(id) => ctx.onEvent({ type: "HOTSPOT_OPENED", blockId: b.id, hotspotId: id })} />;
    case "geometry": {
      const g = readGeometrySpec(b.spec);
      if (!g) return <div style={{ padding: 12, borderRadius: 12, background: C.amberTint, color: C.amberText, fontSize: 13 }}>Figure de géométrie à corriger dans le studio.</div>;
      return guard(<GeometryFigure spec={g} params={ctx.params} lang={ctx.lang} onParam={(name, v) => { ctx.setParam(name, v); ctx.onEvent({ type: "PARAM_CHANGED", name, value: v }); }} />);
    }
    case "solid3d": {
      const sp = ctx.solids.get(b.id);
      if (!sp) return null;
      return guard(
        <Suspense fallback={<Loading3D />}>
          <Solid3D spec={sp} params={ctx.params} lang={ctx.lang}
            onParam={(name, v) => { ctx.setParam(name, v); ctx.onEvent({ type: "PARAM_CHANGED", name, value: v }); }}
            onInteract={() => ctx.onEvent({ type: "SCENE_ROTATED", blockId: b.id })} />
        </Suspense>,
      );
    }
    case "scene3d":
      return guard(
        <Suspense fallback={<Loading3D />}>
          <Model3D src={ctx.resolveAsset(b.assetId)} poster={b.fallback2dAssetId ? ctx.resolveAsset(b.fallback2dAssetId) : null}
            sizeBytes={b.sizeBytes} hotspots={b.hotspots} lang={ctx.lang} autoLoad={ctx.autoLoad3d} credit={b.credit ?? null} partLabels={b.parts}
            onHotspot={(id) => ctx.onEvent({ type: "HOTSPOT_OPENED", blockId: b.id, hotspotId: id })}
            onInteract={() => ctx.onEvent({ type: "SCENE_ROTATED", blockId: b.id })} />
        </Suspense>,
      );
    case "mistake": return <div key={animKey} className="jg-in" style={{ background: C.amberTint, borderRadius: 12, padding: 12, fontSize: 14, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: md(b.md) }} />;
    case "quiz": return <QuickCheck key={animKey} block={b} compact={ctx.compact} onAnswer={(correct) => ctx.onEvent({ type: "QUICK_CHECK", blockId: b.id, correct })} />;
    case "video": {
      const src = ctx.resolveAsset(b.assetId);
      const meta = `Vidéo optionnelle · ${Math.max(1, Math.round(b.durationSec / 60))} min · ${(b.sizeBytes / 1e6).toFixed(1).replace(".", ",")} Mo`;
      return (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: src ? 8 : 12, fontSize: 13, color: C.muted, display: "flex", flexDirection: "column", gap: 6 }}>
          {/* preload="none" : rien n'est téléchargé tant que l'élève n'appuie pas sur lecture (forfaits mobiles). */}
          {src && <video src={src} controls preload="none" playsInline style={{ width: "100%", borderRadius: 8, background: C.ink, aspectRatio: "16 / 9" }} />}
          <span>{meta}</span>
        </div>
      );
    }
    default: return null;
  }
}
